import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Copy, Pencil, Trash2, Power, Eye } from "lucide-react";
import { api } from "@/lib/api";
import { inr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useApp } from "@/context/AppContext";

const TYPES = ["Flat Rate", "Order Slab", "Custom"];
const emptySlab = () => ({ min_orders: "", max_orders: "", rate: "" });

export default function EarningModels() {
  const { can } = useApp();
  const canWrite = can("set.edit_models") || can("set.create_models");
  const [models, setModels] = useState([]);
  const [open, setOpen] = useState(false);
  const [editModel, setEditModel] = useState(null);
  const [viewModel, setViewModel] = useState(null);
  const [delModel, setDelModel] = useState(null);

  const load = () => api.get("/earning-models").then(({ data }) => setModels(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = async (m) => {
    try { await api.post(`/earning-models/${m.id}/toggle`); toast.success(m.active ? "Deactivated" : "Activated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const duplicate = async (m) => {
    try { await api.post(`/earning-models/${m.id}/duplicate`); toast.success("Model duplicated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const doDelete = async () => {
    try { await api.delete(`/earning-models/${delModel.id}`); toast.success("Model deleted"); setDelModel(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="earning-models-panel">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Earning plans control how each Delivery Partner's daily earnings are calculated.</p>
        {canWrite && <Button size="sm" onClick={() => { setEditModel(null); setOpen(true); }} data-testid="create-model-btn"><Plus className="mr-1 h-4 w-4" /> Create Earning Model</Button>}
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            <TableHead>Model Name</TableHead><TableHead>Type</TableHead><TableHead>Rate / Slabs</TableHead><TableHead>Multi Order</TableHead>
            <TableHead className="text-right">Active DPs</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {models.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">No earning models yet. Create your first model.</TableCell></TableRow>}
            {models.map((m) => (
              <TableRow key={m.id} data-testid={`model-row-${m.id}`}>
                <TableCell className="font-semibold">{m.name}</TableCell>
                <TableCell className="text-xs">{m.type}</TableCell>
                <TableCell className="tabular text-sm">
                  {m.type === "Flat Rate" ? inr(m.rate) + "/order" : m.type === "Order Slab" ? `${(m.slabs || []).length} slabs` : `Base ${inr(m.custom?.base_rate)}`}
                </TableCell>
                <TableCell className="tabular text-xs" data-testid={`model-multi-${m.id}`}>{multiLabel(m)}</TableCell>
                <TableCell className="text-right tabular font-semibold">{m.active_dps ?? 0}</TableCell>
                <TableCell><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${m.active ? "bg-emerald-500/10 text-emerald-500" : "bg-secondary text-muted-foreground"}`} data-testid={`model-status-${m.id}`}>{m.active ? "Active" : "Inactive"}</span></TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-0.5">
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="View" onClick={() => setViewModel(m)} data-testid={`view-model-${m.id}`}><Eye className="h-4 w-4" /></Button>
                    {canWrite && <>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Edit" onClick={() => { setEditModel(m); setOpen(true); }} data-testid={`edit-model-${m.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Duplicate" onClick={() => duplicate(m)} data-testid={`duplicate-model-${m.id}`}><Copy className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" className={`h-8 w-8 p-0 ${m.active ? "text-amber-500" : "text-emerald-500"}`} title={m.active ? "Deactivate" : "Activate"} onClick={() => toggle(m)} data-testid={`toggle-model-${m.id}`}><Power className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-500" title="Delete" onClick={() => setDelModel(m)} data-testid={`delete-model-${m.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <ModelDialog open={open} onClose={() => setOpen(false)} model={editModel} onSaved={load} />
      <ViewDialog model={viewModel} onClose={() => setViewModel(null)} />
      <AlertDialog open={!!delModel} onOpenChange={(o) => !o && setDelModel(null)}>
        <AlertDialogContent data-testid="delete-model-dialog">
          <AlertDialogHeader><AlertDialogTitle>Delete "{delModel?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>Only possible if no Delivery Partner uses this model. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-model">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ModelDialog({ open, onClose, model, onSaved }) {
  const isEdit = !!model;
  const [name, setName] = useState("");
  const [type, setType] = useState("Flat Rate");
  const [rate, setRate] = useState("");
  const [slabs, setSlabs] = useState([emptySlab()]);
  const [custom, setCustom] = useState({ base_rate: "", rain_enabled: false, rain_bonus: "", distance_enabled: false, distance_bonus: "", peak_hour_bonus: "", extra_incentive: "", other_adjustment: "" });
  const [saving, setSaving] = useState(false);
  const [multiOn, setMultiOn] = useState(false);
  const [multiRate, setMultiRate] = useState("");

  useEffect(() => {
    if (!open) return;
    setMultiOn(!!model?.multi_order_enabled);
    setMultiRate(model?.multi_order_rate ?? "");
    if (model) {
      setName(model.name); setType(model.type); setRate(model.rate ?? "");
      setSlabs((model.slabs && model.slabs.length ? model.slabs.map((s) => ({ min_orders: s.min_orders, max_orders: s.max_orders ?? "", rate: s.rate })) : [emptySlab()]));
      setCustom({ base_rate: "", rain_enabled: false, rain_bonus: "", distance_enabled: false, distance_bonus: "", peak_hour_bonus: "", extra_incentive: "", other_adjustment: "", ...(model.custom || {}) });
    } else {
      setName(""); setType("Flat Rate"); setRate(""); setSlabs([emptySlab()]);
      setCustom({ base_rate: "", rain_enabled: false, rain_bonus: "", distance_enabled: false, distance_bonus: "", peak_hour_bonus: "", extra_incentive: "", other_adjustment: "" });
    }
  }, [open, model]);

  const setSlab = (i, k, v) => setSlabs((s) => s.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const setC = (k, v) => setCustom((s) => ({ ...s, [k]: v }));

  // client-side slab validation for instant feedback
  const slabError = (() => {
    if (type !== "Order Slab") return "";
    const list = slabs.map((s) => ({ min: Number(s.min_orders), max: s.max_orders === "" ? null : Number(s.max_orders), rate: Number(s.rate) }));
    if (list.some((s) => isNaN(s.min) || isNaN(s.rate))) return "Every slab needs min orders and a rate.";
    const sorted = [...list].sort((a, b) => a.min - b.min);
    for (let i = 0; i < sorted.length - 1; i++) {
      if (sorted[i].max === null) return "Only the final slab may be open-ended.";
      if (sorted[i].max < sorted[i].min) return "Max orders cannot be less than min orders.";
      if (sorted[i + 1].min <= sorted[i].max) return `Slabs overlap: ${sorted[i].min}-${sorted[i].max} and ${sorted[i + 1].min}-${sorted[i + 1].max ?? "∞"}.`;
    }
    return "";
  })();

  const save = async () => {
    if (!name.trim()) { toast.error("Model name is required"); return; }
    if (type === "Flat Rate" && !rate) { toast.error("Enter a rate per order"); return; }
    if (type === "Order Slab" && slabError) { toast.error(slabError); return; }
    if (type === "Custom" && !custom.base_rate) { toast.error("Enter a base rate per order"); return; }
    setSaving(true);
    const payload = { name: name.trim(), type, multi_order_enabled: multiOn, multi_order_rate: multiRate === "" ? null : Number(multiRate) };
    if (type === "Flat Rate") payload.rate = Number(rate);
    if (type === "Order Slab") payload.slabs = slabs.map((s) => ({ min_orders: Number(s.min_orders), max_orders: s.max_orders === "" ? null : Number(s.max_orders), rate: Number(s.rate) }));
    if (type === "Custom") payload.custom = { ...custom, base_rate: Number(custom.base_rate), rain_bonus: Number(custom.rain_bonus || 0), distance_bonus: Number(custom.distance_bonus || 0) };
    try {
      if (isEdit) await api.put(`/earning-models/${model.id}`, payload);
      else await api.post("/earning-models", payload);
      toast.success(isEdit ? "Model updated" : "Earning model created");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-card" data-testid="model-dialog">
        <DialogHeader><DialogTitle className="font-head">{isEdit ? "Edit" : "Create"} Earning Model</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Model Name *</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Rs. 60 Flat Model" data-testid="model-name" /></div>
          <div><Label>Model Type *</Label>
            <Select value={type} onValueChange={setType} disabled={isEdit}>
              <SelectTrigger data-testid="model-type"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
        </div>

        {type === "Flat Rate" && (
          <div className="mt-3 max-w-xs">
            <Label>Rate Per Order (₹) *</Label>
            <Input type="number" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="60" data-testid="model-flat-rate" />
            {rate && <p className="mt-1 text-xs text-muted-foreground">100 orders × {inr(Number(rate))} = {inr(Number(rate) * 100)}</p>}
          </div>
        )}

        {type === "Order Slab" && (
          <div className="mt-3">
            <div className="mb-2 flex items-center justify-between">
              <Label className="mb-0">Order Slabs</Label>
              <Button size="sm" variant="outline" onClick={() => setSlabs((s) => [...s, emptySlab()])} data-testid="add-slab-row"><Plus className="mr-1 h-3.5 w-3.5" /> Add Slab</Button>
            </div>
            <div className="space-y-2">
              {slabs.map((s, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`slab-input-row-${i}`}>
                  <Input type="number" className="w-24" placeholder="Min" value={s.min_orders} onChange={(e) => setSlab(i, "min_orders", e.target.value)} />
                  <span className="text-muted-foreground">–</span>
                  <Input type="number" className="w-24" placeholder="Max (∞)" value={s.max_orders} onChange={(e) => setSlab(i, "max_orders", e.target.value)} />
                  <span className="text-xs text-muted-foreground">orders @ ₹</span>
                  <Input type="number" className="w-24" placeholder="Rate" value={s.rate} onChange={(e) => setSlab(i, "rate", e.target.value)} />
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-500" onClick={() => setSlabs((x) => x.filter((_, j) => j !== i))} disabled={slabs.length === 1}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
            {slabError && <p className="mt-2 text-xs font-medium text-rose-500" data-testid="slab-error">{slabError}</p>}
            {!slabError && <p className="mt-2 text-xs text-muted-foreground">Rate applies to total completed orders (e.g. 50 orders in a 45+ @ ₹70 slab → 50 × ₹70 = ₹3,500).</p>}
          </div>
        )}

        {type === "Custom" && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div><Label>Base Rate Per Order (₹) *</Label><Input type="number" value={custom.base_rate} onChange={(e) => setC("base_rate", e.target.value)} data-testid="model-custom-base" /></div>
            <div><Label>Peak Hour Bonus (₹)</Label><Input type="number" value={custom.peak_hour_bonus} onChange={(e) => setC("peak_hour_bonus", e.target.value)} /></div>
            <div className="flex items-center justify-between rounded-lg border border-border bg-secondary/40 px-3 py-2">
              <Label className="mb-0">Rain Bonus</Label><Switch checked={custom.rain_enabled} onCheckedChange={(v) => setC("rain_enabled", v)} data-testid="model-rain-toggle" />
            </div>
            {custom.rain_enabled && <div><Label>Rain Bonus (₹/day)</Label><Input type="number" value={custom.rain_bonus} onChange={(e) => setC("rain_bonus", e.target.value)} /></div>}
            <div className="flex items-center justify-between rounded-lg border border-border bg-secondary/40 px-3 py-2">
              <Label className="mb-0">Distance Bonus</Label><Switch checked={custom.distance_enabled} onCheckedChange={(v) => setC("distance_enabled", v)} data-testid="model-distance-toggle" />
            </div>
            {custom.distance_enabled && <div><Label>Distance Bonus (₹/km)</Label><Input type="number" value={custom.distance_bonus} onChange={(e) => setC("distance_bonus", e.target.value)} /></div>}
          </div>
        )}

        <div className="mt-4 rounded-lg border border-border bg-secondary/40 p-3" data-testid="model-multi-section">
          <div className="flex items-center justify-between">
            <div><p className="text-sm font-medium">Multi-Order Rate</p><p className="text-xs text-muted-foreground">Slabs are picked by total orders; normal orders earn the slab rate, multi orders earn this fixed rate. Off = multi orders paid like normal orders.</p></div>
            <Switch checked={multiOn} onCheckedChange={setMultiOn} data-testid="model-multi-toggle" />
          </div>
          {multiOn && (
            <div className="mt-2 max-w-xs">
              <Label>Rate per Multi Order (₹)</Label>
              <Input type="number" min="0" value={multiRate} onChange={(e) => setMultiRate(e.target.value)} placeholder="Blank = global default" data-testid="model-multi-rate" />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-model-btn">{saving ? "Saving…" : isEdit ? "Save Changes" : "Create Model"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ViewDialog({ model, onClose }) {
  return (
    <Dialog open={!!model} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card" data-testid="view-model-dialog">
        {model && <>
          <DialogHeader><DialogTitle className="font-head">{model.name}</DialogTitle></DialogHeader>
          <div className="space-y-1.5 text-sm">
            <Row k="Type" v={model.type} />
            <Row k="Status" v={model.active ? "Active" : "Inactive"} />
            <Row k="Active DPs" v={model.active_dps ?? 0} />
            <Row k="Multi Order" v={multiLabel(model)} />
            {model.type === "Flat Rate" && <Row k="Rate" v={`${inr(model.rate)}/order`} />}
            {model.type === "Custom" && <Row k="Base Rate" v={`${inr(model.custom?.base_rate)}/order`} />}
            <Row k="Created By" v={model.created_by || "—"} />
          </div>
          {model.type === "Order Slab" && (
            <div className="mt-3 rounded-lg border border-border">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Orders</TableHead><TableHead className="text-right">Rate</TableHead></TableRow></TableHeader>
                <TableBody>
                  {(model.slabs || []).map((s, i) => (
                    <TableRow key={i}><TableCell>{s.min_orders} – {s.max_orders ?? "∞"}</TableCell><TableCell className="text-right tabular">{inr(s.rate)}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <DialogFooter><Button onClick={onClose}>Close</Button></DialogFooter>
        </>}
      </DialogContent>
    </Dialog>
  );
}
const multiLabel = (m) => !m.multi_order_enabled ? "Off (normal rate)" : m.multi_order_rate != null ? `${inr(m.multi_order_rate)}/multi` : "Global default";
const Row = ({ k, v }) => <div className="flex items-center justify-between border-b border-border/50 py-1"><span className="text-muted-foreground">{k}</span><span className="font-medium">{v}</span></div>;
